import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCurrency } from "@/lib/currency";

const DEFAULTS = {
  historical_annual_rent: 0,
  expected_occupancy_pct: 92,
  expected_rent_growth_pct: 3,
  monthly_maintenance_cost: 0,
  expected_annual_return_pct: 8,
  duration_months: 120,
  amc_monthly_fee_pct: 5,
  amc_setup_fee_pct: 2,
  total_coins: 100000,
  comment: "",
};

export default function AmcDashboard() {
  const { t } = useTranslation();
  const { fmt } = useCurrency();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [form, setForm] = useState(DEFAULTS);

  const load = useCallback(() => api.get("/amc/incoming").then(({ data }) => setItems(data)), []);
  useEffect(() => { load(); }, [load]);

  const openForm = (p) => {
    setTarget(p);
    let f = { ...DEFAULTS, historical_annual_rent: p.monthly_rent * 12 };
    // Pull calculator scenario if present
    try {
      const raw = sessionStorage.getItem("lm_scenario");
      if (raw) {
        const s = JSON.parse(raw);
        f = {
          ...f,
          historical_annual_rent: Number(s.annualNet) || f.historical_annual_rent,
          expected_annual_return_pct: Number(s.investorDiscount) / Math.max(Number(s.yearsOffered), 1) || f.expected_annual_return_pct,
          duration_months: Number(s.yearsOffered) * 12 || f.duration_months,
          amc_monthly_fee_pct: Number(s.amcMonthlyPct) || f.amc_monthly_fee_pct,
          amc_setup_fee_pct: Number(s.amcSetupPct) || f.amc_setup_fee_pct,
        };
        toast.success(t("scenario_loaded"));
        sessionStorage.removeItem("lm_scenario");
      }
    } catch (err) { console.warn("Scenario prefill failed:", err); }
    setForm(f);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, k === "comment" ? v : Number(v)])
      );
      payload.comment = form.comment;
      await api.post(`/properties/${target.id}/feasibility`, payload);
      toast.success("✓");
      setOpen(false); load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const previewMath = () => {
    if (!target) return null;
    const occ = Number(form.expected_occupancy_pct) / 100;
    const effectiveRent = target.monthly_rent * occ - Number(form.monthly_maintenance_cost);
    const amcMonthly = effectiveRent * (Number(form.amc_monthly_fee_pct) / 100);
    const netInvestors = effectiveRent - amcMonthly;
    const years = Number(form.duration_months) / 12;
    const apy = Number(form.expected_annual_return_pct) / 100;
    const totalNetRev = netInvestors * Number(form.duration_months);
    const targetRaise = totalNetRev / (1 + apy * years);
    const setupFee = targetRaise * (Number(form.amc_setup_fee_pct) / 100);
    const landlordAdvance = targetRaise - setupFee;
    const pricePerCoin = Number(form.total_coins) > 0 ? targetRaise / Number(form.total_coins) : 0;
    return { effectiveRent, amcMonthly, netInvestors, targetRaise, setupFee, landlordAdvance, pricePerCoin };
  };
  const math = previewMath();

  const FIELDS = [
    ["historical_annual_rent",   t("amc_f_hist_rent")],
    ["expected_occupancy_pct",   t("amc_f_occupancy")],
    ["expected_rent_growth_pct", t("amc_f_growth")],
    ["monthly_maintenance_cost", t("amc_f_maint")],
    ["expected_annual_return_pct", t("amc_f_apy")],
    ["duration_months",          t("amc_f_duration")],
    ["amc_monthly_fee_pct",      t("amc_f_monthly_fee")],
    ["amc_setup_fee_pct",        t("amc_f_setup_fee")],
    ["total_coins",              t("amc_f_total_coins")],
  ];

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-12">
      <div className="font-mono-label text-[11px] text-[#002FA7]">⎯⎯ {t("amc_tag")}</div>
      <h1 className="font-display text-5xl font-black mt-2">{t("amc_title")}</h1>
      <p className="text-zinc-600 mt-3 max-w-2xl">{t("amc_sub")}</p>

      {items.length === 0 ? (
        <div className="border border-black/10 p-16 text-center mt-10">
          <div className="font-mono-label text-[11px] text-zinc-500">{t("amc_empty")}</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-10">
          {items.map((p) => (
            <div key={p.id} className="border border-black/10 bg-white overflow-hidden">
              <div className="grid grid-cols-3">
                <div className="aspect-square">
                  <img src={p.image_url} alt="" className="w-full h-full object-cover"/>
                </div>
                <div className="col-span-2 p-5 min-w-0">
                  <div className="flex justify-between items-start gap-2">
                    <div className="min-w-0">
                      <div className="font-display text-xl font-bold truncate">{p.name}</div>
                      <div className="text-xs text-zinc-500 mt-1 truncate">{p.address}</div>
                    </div>
                    {p.ico_id ? (
                      <Badge className="rounded-none bg-black font-mono-label text-[10px] shrink-0">{t("ll_status_ico_live")}</Badge>
                    ) : (
                      <Badge className="rounded-none bg-[#FF3B30] font-mono-label text-[10px] shrink-0">{t("amc_pending")}</Badge>
                    )}
                  </div>

                  <div className="grid grid-cols-3 gap-px bg-black/10 mt-4">
                    <div className="bg-white p-2 min-w-0">
                      <div className="font-mono-label text-[9px] text-zinc-500 truncate">{t("amc_units")}</div>
                      <div className="font-display text-base font-bold tabular-nums">{(p.units || []).length || 1}</div>
                    </div>
                    <div className="bg-white p-2 min-w-0">
                      <div className="font-mono-label text-[9px] text-zinc-500 truncate">{t("ll_agg_rent")}</div>
                      <div className="font-display text-base font-bold tabular-nums truncate">{fmt(p.monthly_rent)}</div>
                    </div>
                    <div className="bg-white p-2 min-w-0">
                      <div className="font-mono-label text-[9px] text-zinc-500 truncate">{t("amc_owner")}</div>
                      <div className="text-xs truncate">{p.owner_name}</div>
                    </div>
                  </div>

                  <div className="flex gap-2 mt-4 flex-wrap">
                    {!p.ico_id && (
                      <Button data-testid={`feas-${p.id}`} onClick={() => openForm(p)}
                        className="rounded-none h-9 bg-[#002FA7] hover:bg-[#001F70] font-mono-label text-[10px]">
                        {t("amc_publish")}
                      </Button>
                    )}
                    {p.ico_id && (
                      <Link to={`/ico/${p.ico_id}`}>
                        <Button variant="outline" className="rounded-none h-9 border-black font-mono-label text-[10px]">
                          {t("amc_view_ico")}
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">
              {t("amc_publish_dialog")} · {target?.name}
            </DialogTitle>
          </DialogHeader>

          {target && (
            <form onSubmit={submit} className="grid grid-cols-2 gap-4">
              <div className="col-span-2 text-xs text-zinc-600 bg-zinc-50 p-3 border border-black/10">
                {t("amc_agg_rent")}: <b className="tabular-nums">{fmt(target.monthly_rent)}</b> ·
                {" "}{t("amc_units")}: <b className="tabular-nums">{(target.units || []).length || 1}</b> ·
                {" "}{t("amc_owner")}: <b>{target.owner_name}</b>
              </div>

              {FIELDS.map(([k, l]) => (
                <div key={k} className="min-w-0">
                  <Label className="font-mono-label text-[10px]">{l}</Label>
                  <Input data-testid={`feas-${k}`} type="number" step="0.01" required
                    value={form[k]}
                    onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    className="rounded-none h-10 mt-1"/>
                </div>
              ))}

              <div className="col-span-2">
                <Label className="font-mono-label text-[10px]">{t("amc_f_comment")}</Label>
                <Textarea data-testid="feas-comment" value={form.comment}
                  onChange={(e) => setForm({ ...form, comment: e.target.value })}
                  className="rounded-none mt-1" rows={3} placeholder={t("amc_f_comment_ph")}/>
              </div>

              {math && (
                <div className="col-span-2 border border-black p-4 bg-black text-white">
                  <div className="font-mono-label text-[10px] text-[#FFCC00] mb-3">⎯⎯ {t("amc_math_title")}</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    {[
                      [t("amc_math_eff"), fmt(math.effectiveRent)],
                      [t("amc_math_net"), fmt(math.netInvestors)],
                      [t("amc_math_raise"), fmt(math.targetRaise)],
                      [t("amc_math_price"), fmt(math.pricePerCoin, { maxDecimals: 2 })],
                      [t("amc_math_advance"), fmt(math.landlordAdvance)],
                      [t("amc_math_setup"), fmt(math.setupFee)],
                      [t("amc_math_monthly"), fmt(math.amcMonthly)],
                      [t("amc_math_apy"), `${Number(form.expected_annual_return_pct).toFixed(1)}%`],
                    ].map(([k, v]) => (
                      <div key={k} className="min-w-0">
                        <div className="font-mono-label text-[9px] text-zinc-400 truncate">{k}</div>
                        <div className="font-display font-bold tabular-nums truncate">{v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <DialogFooter className="col-span-2">
                <Button data-testid="feas-submit" type="submit" className="rounded-none bg-[#002FA7] hover:bg-[#001F70]">
                  {t("amc_submit")}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
